import React, { useState, useEffect } from 'react';
import { useLocalization } from '../../contexts/LocalizationContext';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  User,
  ShoppingBag,
  Heart,
  MapPin,
  LogOut,
  ChevronRight,
  Package,
  Star,
  MessageSquare
} from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '../ui/sheet';
import { Button } from '../ui/button';
import { ScrollArea } from '../ui/scroll-area';
import { Avatar, AvatarFallback } from '../ui/avatar';
import { Separator } from '../ui/separator';
import { wishlistApi, ordersApi } from '../../services/api';
import { useCurrentUser, invalidateCurrentUser } from '../../hooks/useCurrentUser';

const AccountSidebar = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const { countryCode } = useLocalization();
  const navigate = useNavigate();
  const { user } = useCurrentUser();
  const [stats, setStats] = useState({
    ordersCount: 0,
    wishlistCount: 0
  });

  useEffect(() => {
    if (isOpen) {
      loadStats();
    }
  }, [isOpen]);

  const loadStats = async () => {
    const token = localStorage.getItem('accessToken');
    if (!token) return;

    try {
      // Fetch wishlist count (no countryCode needed)
      const wishlistData = await wishlistApi.getWishlist(token, countryCode);
      const wishlistCount = wishlistData.items?.length || 0;

      // Fetch orders count (token is the 3rd parameter)
      const ordersData = await ordersApi.getOrders(1, 100, token);
      const ordersCount = ordersData.orders?.length || ordersData.total || 0;

      setStats({
        ordersCount,
        wishlistCount
      });
    } catch (apiError) {
      console.error('Error fetching stats:', apiError);
      setStats({
        ordersCount: 0,
        wishlistCount: 0
      });
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    invalidateCurrentUser();
    onClose();
    navigate('/');
    window.location.reload();
  };

  const handleNavigate = (path) => {
    onClose();
    navigate(path);
  };

  const getInitials = () => {
    if (!user) return 'U';
    const first = user.firstName?.[0] || '';
    const last = user.lastName?.[0] || '';
    return (first + last).toUpperCase() || user.email?.[0]?.toUpperCase() || 'U';
  };

  const menuItems = [
    {
      icon: ShoppingBag,
      label: t('account.orders'),
      path: '/account/orders',
      badge: stats.ordersCount > 0 ? stats.ordersCount : null
    },
    {
      icon: Star,
      label: t('account.reviews'),
      path: '/account/reviews'
    },
    {
      icon: MessageSquare,
      label: t('account.support'),
      path: '/account/support'
    },
    {
      icon: Heart,
      label: t('account.wishlist'),
      path: '/account/wishlist',
      badge: stats.wishlistCount > 0 ? stats.wishlistCount : null
    },
    {
      icon: MapPin,
      label: t('account.addresses'),
      path: '/account/addresses'
    },
    {
      icon: User,
      label: t('account.profile'),
      path: '/account/profile'
    }
  ];

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent side="right" className="w-full sm:max-w-md">
        <SheetHeader>
          <SheetTitle className="text-left">
            {t('account.myAccount')}
          </SheetTitle>
        </SheetHeader>

        <ScrollArea className="h-[calc(100vh-80px)] pr-4">
          {/* User Profile Section */}
          <div className="py-6">
            <div className="flex items-center gap-4 mb-6">
              <Avatar className="h-16 w-16 bg-[#be1f1a] text-white">
                <AvatarFallback className="bg-[#be1f1a] text-white text-xl">
                  {getInitials()}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1">
                <h3 className="font-semibold text-lg text-[#2C2420]">
                  {user?.firstName && user?.lastName 
                    ? `${user.firstName} ${user.lastName}` 
                    : user?.email?.split('@')[0] || t('account.profile')}
                </h3>
                <p className="text-sm text-gray-500">{user?.email}</p>
              </div>
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className="bg-[#FAF8F5] rounded-lg p-4 text-center">
                <Package className="h-6 w-6 text-[#be1f1a] mx-auto mb-2" />
                <div className="text-2xl font-bold text-[#2C2420]">
                  {stats.ordersCount}
                </div>
                <div className="text-xs text-gray-500">
                  {t('account.orders')}
                </div>
              </div>
              <div className="bg-[#FAF8F5] rounded-lg p-4 text-center">
                <Heart className="h-6 w-6 text-[#be1f1a] mx-auto mb-2" />
                <div className="text-2xl font-bold text-[#2C2420]">
                  {stats.wishlistCount}
                </div>
                <div className="text-xs text-gray-500">
                  {t('account.wishlist')}
                </div>
              </div>
            </div>
          </div>

          <Separator className="my-4" />

          {/* Menu Items */}
          <div className="space-y-2 py-4">
            {menuItems.map((item, index) => (
              <button
                key={index}
                onClick={() => handleNavigate(item.path)}
                className="w-full flex items-center justify-between p-3 rounded-lg hover:bg-[#FAF8F5] transition-colors group"
              >
                <div className="flex items-center gap-3">
                  <item.icon className="h-5 w-5 text-gray-600 group-hover:text-[#be1f1a]" />
                  <span className="text-sm font-medium text-[#2C2420]">
                    {item.label}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  {item.badge && (
                    <span className="px-2 py-1 text-xs font-semibold bg-[#be1f1a] text-white rounded-full">
                      {item.badge}
                    </span>
                  )}
                  <ChevronRight className="h-4 w-4 text-gray-400 group-hover:text-[#be1f1a]" />
                </div>
              </button>
            ))}
          </div>

          <Separator className="my-4" />

          {/* Logout Button */}
          <div className="py-4">
            <Button
              onClick={handleLogout}
              variant="outline"
              className="w-full justify-start gap-3 border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
            >
              <LogOut className="h-5 w-5" />
              {t('auth.logout')}
            </Button>
          </div>

          {/* Footer Info */}
          <div className="py-4 text-center text-xs text-gray-500">
            <p>{t('account.memberSince')} {new Date().getFullYear()}</p>
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
};

export default AccountSidebar;
